import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { fetchJobsByTask, isOffQueueStatus, type AiJob } from '../../lib/aiJobs';
import { AI_JOB_STATUS_LABEL, aiJobStatusTone } from '../../lib/aiJobLabels';
import { Badge, Button, Icon, IconButton, SidePanel } from '../ui';
import { faLinkSlash } from '@fortawesome/free-solid-svg-icons';
import SpecQueueFromTarget from '../ai/SpecQueueFromTarget';
import TrelloCardDetailBody from './TrelloCardDetailBody';
import DeleteConfirmationModal from '../clientDetail/sections/functions/DeleteConfirmationModal';
import { CommsItem, KIND_LABELS, timeAgo, age } from '../../lib/comms';
import type { BadgeTone } from '../ui';
import type { CommsKind } from '../../lib/comms';

const KIND_TONES: Record<CommsKind, BadgeTone> = {
  email: 'info',
  card_created: 'accent',
  comment: 'info',
  description_changed: 'warn',
  card_moved: 'up',
  card_renamed: 'neutral',
  attachment_added: 'idle',
  manual_import: 'info',
};

interface CardThread {
  cardId: string;
  cardName: string;
  cardUrl: string;
  boardName: string;
  completedAt: string | null; // link-level "done" flag; never clears the card
  items: CommsItem[]; // newest first
}

interface CommsEventChipProps {
  /** The task to look up — renders nothing unless the task links comms events */
  taskId: string | null | undefined;
  style?: React.CSSProperties;
  onEnqueued?: () => void;
  /** Called after a chip is removed (its comms_task_links row deleted). */
  onRemoved?: (cardId: string) => void;
}

/**
 * Little cards shown on task detail views for each Client Comms event
 * attached to the task (via comms_task_links — a task can hold several).
 * Self-sufficient: give it a task id, it renders one chip per linked card
 * and opens that card's conversation in its own SidePanel on click.
 * Renders nothing when there are no links.
 */
export default function CommsEventChip({ taskId, style, onEnqueued, onRemoved }: CommsEventChipProps) {
  const [threads, setThreads] = useState<CardThread[]>([]);
  const [openCardId, setOpenCardId] = useState<string | null>(null);
  const [queuedByCard, setQueuedByCard] = useState<Map<string, AiJob>>(new Map());
  const [pendingRemove, setPendingRemove] = useState<CardThread | null>(null);
  const [removingCardId, setRemovingCardId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setThreads([]);
    setOpenCardId(null);
    setQueuedByCard(new Map());
    if (!taskId) return;

    const loadJobs = async () => {
      try {
        const rows = await fetchJobsByTask(taskId);
        if (cancelled) return;
        const map = new Map<string, AiJob>();
        for (const job of rows) {
          if (job.card_id && !isOffQueueStatus(job.status)) {
            map.set(job.card_id, job);
          }
        }
        setQueuedByCard(map);
      } catch (err) {
        console.error('Error loading AI jobs for comms chips:', err);
      }
    };

    const load = async () => {
      const { data: links, error: linksError } = await supabase
        .from('comms_task_links')
        .select('card_id, completed_at')
        .eq('task_id', taskId);
      const linkRows = (links as Array<{ card_id: string; completed_at: string | null }> | null) || [];
      const cardIds = linkRows.map(l => l.card_id);
      const completedByCard = new Map(linkRows.map(l => [l.card_id, l.completed_at]));
      if (cancelled || linksError || cardIds.length === 0) return;

      const { data, error } = await supabase
        .from('comms_items')
        .select('*, comms_boards(board_name, client_id)')
        .in('card_id', cardIds)
        .order('occurred_at', { ascending: false })
        .limit(300);
      if (cancelled || error || !data || data.length === 0) return;

      const byCard = new Map<string, CommsItem[]>();
      (data as CommsItem[]).forEach(item => {
        const existing = byCard.get(item.card_id) || [];
        existing.push(item);
        byCard.set(item.card_id, existing);
      });

      const result: CardThread[] = [];
      byCard.forEach((items, cardId) => {
        result.push({
          cardId,
          cardName: items[0].card_name,
          cardUrl: items[0].card_url,
          boardName: items[0].comms_boards?.board_name || '—',
          completedAt: completedByCard.get(cardId) ?? null,
          items,
        });
      });
      // Most recently active card first
      result.sort(
        (a, b) => new Date(b.items[0].occurred_at).getTime() - new Date(a.items[0].occurred_at).getTime()
      );
      setThreads(result);
    };
    load();
    loadJobs();
    return () => {
      cancelled = true;
    };
  }, [taskId]);

  const toggleComplete = async (thread: CardThread) => {
    if (!taskId) return;
    const next = thread.completedAt ? null : new Date().toISOString();
    // Optimistic flip; revert on failure
    setThreads(prev => prev.map(t => (t.cardId === thread.cardId ? { ...t, completedAt: next } : t)));
    const { error } = await supabase
      .from('comms_task_links')
      .update({ completed_at: next })
      .eq('card_id', thread.cardId)
      .eq('task_id', taskId);
    if (error) {
      console.error('Error toggling comms attachment completion:', error);
      setThreads(prev => prev.map(t => (t.cardId === thread.cardId ? { ...t, completedAt: thread.completedAt } : t)));
    }
  };

  // Detach the card from the task (deletes the comms_task_links row — same as
  // Detach in Client Comms). The card itself and its events are untouched, so
  // it goes back to the comms tables and can be re-attached from there.
  // Refused while an agent job is bound to the card: the job's card binding
  // would dangle, and submitApprovedBuild would have no link to tick.
  const requestRemove = (thread: CardThread) => {
    if (!taskId || removingCardId) return;
    if (queuedByCard.has(thread.cardId)) return;
    setPendingRemove(thread);
  };

  const confirmRemove = async () => {
    const thread = pendingRemove;
    if (!thread || !taskId || removingCardId) return;
    setRemovingCardId(thread.cardId);
    const { error } = await supabase
      .from('comms_task_links')
      .delete()
      .eq('card_id', thread.cardId)
      .eq('task_id', taskId);
    setRemovingCardId(null);
    setPendingRemove(null);
    if (error) {
      console.error('Error removing comms card from task:', error);
      return;
    }
    setThreads(prev => prev.filter(t => t.cardId !== thread.cardId));
    setOpenCardId(prev => (prev === thread.cardId ? null : prev));
    onRemoved?.(thread.cardId);
  };

  const removeBlockedTitle = (thread: CardThread): string | undefined => {
    const job = queuedByCard.get(thread.cardId);
    if (job) return `Cancel the agent job (${AI_JOB_STATUS_LABEL[job.status]}) before removing this card`;
    return undefined;
  };

  const handleEnqueued = () => {
    if (taskId) {
      fetchJobsByTask(taskId)
        .then((rows) => {
          const map = new Map<string, AiJob>();
          for (const job of rows) {
            if (job.card_id && !isOffQueueStatus(job.status)) {
              map.set(job.card_id, job);
            }
          }
          setQueuedByCard(map);
        })
        .catch((err) => console.error('Error refreshing AI jobs for comms chips:', err));
    }
    onEnqueued?.();
  };

  if (threads.length === 0) return null;

  const openThread = threads.find(t => t.cardId === openCardId) ?? null;

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', ...style }}>
        {threads.map(thread => {
          const queued = queuedByCard.get(thread.cardId);
          return (
          <div
            key={thread.cardId}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              width: '100%',
              padding: '10px 12px',
              background: queued ? 'var(--accent-soft)' : 'var(--surface-raised)',
              border: '1px solid var(--border-hairline)',
              borderRadius: 'var(--radius-md)',
            }}
          >
            <button
              type="button"
              className="pressable"
              title={thread.completedAt ? 'Mark incomplete' : 'Mark complete (does not clear the comms card)'}
              aria-pressed={!!thread.completedAt}
              onClick={() => toggleComplete(thread)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '20px',
                height: '20px',
                borderRadius: '50%',
                border: '1.5px solid ' + (thread.completedAt ? 'var(--status-up)' : 'var(--border-strong)'),
                background: thread.completedAt ? 'var(--status-up)' : 'transparent',
                color: 'var(--surface-card)',
                cursor: 'pointer',
                flexShrink: 0,
                padding: 0,
                transition: 'background var(--dur-base) var(--ease-out), border-color var(--dur-base) var(--ease-out)',
              }}
            >
              {thread.completedAt ? <Icon name="check" size={12} /> : null}
            </button>
            <button
              type="button"
              className="hover-wash pressable"
              onClick={() => setOpenCardId(thread.cardId)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                flex: 1,
                minWidth: 0,
                padding: '2px 4px',
                margin: '-2px -4px',
                border: 'none',
                background: 'transparent',
                borderRadius: 'var(--radius-sm)',
                cursor: 'pointer',
                textAlign: 'left',
                fontFamily: 'var(--font-ui)',
                opacity: thread.completedAt ? 0.6 : 1,
              }}
            >
              <Icon name="inbox" size={16} style={{ color: 'var(--accent-text)', flexShrink: 0 }} />
              <span style={{ minWidth: 0, flex: 1 }}>
                <span className="t-eyebrow" style={{ display: 'block', marginBottom: '2px' }}>
                  Client Comms
                </span>
                <span
                  style={{
                    display: 'block',
                    fontSize: 'var(--text-sm)',
                    color: 'var(--text-body)',
                    fontWeight: 500,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    textDecoration: thread.completedAt ? 'line-through' : 'none',
                  }}
                >
                  {thread.cardName}
                </span>
                <span style={{ display: 'block', fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>
                  {thread.completedAt
                    ? `Completed ${age(thread.completedAt)} ago · `
                    : ''}
                  {thread.boardName} · {thread.items.length} event{thread.items.length === 1 ? '' : 's'} · latest {age(thread.items[0].occurred_at)} ago
                </span>
              </span>
              {queued ? (
                <Badge tone={aiJobStatusTone(queued.status)}>{AI_JOB_STATUS_LABEL[queued.status]}</Badge>
              ) : null}
              <Icon name="chevron-right" size={14} style={{ color: 'var(--text-faint)', flexShrink: 0 }} />
            </button>
            {taskId ? (
              <>
                <SpecQueueFromTarget
                  taskId={taskId}
                  title={thread.cardName}
                  cardId={thread.cardId}
                  trigger="bolt"
                  onEnqueued={handleEnqueued}
                />
                <IconButton
                  icon="x"
                  size="sm"
                  label={removeBlockedTitle(thread) ?? 'Remove from task'}
                  disabled={!!queued || removingCardId !== null}
                  onClick={(e) => {
                    e.stopPropagation();
                    requestRemove(thread);
                  }}
                />
              </>
            ) : null}
          </div>
          );
        })}
      </div>

      <SidePanel
        open={!!openThread}
        onClose={() => setOpenCardId(null)}
        eyebrow={openThread?.boardName}
        title={openThread?.cardName}
        width={480}
        layer={1}
        footer={
          openThread ? (
            <span
              style={{
                display: 'flex',
                gap: '8px',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                width: '100%',
              }}
            >
              {taskId ? (
                <span title={removeBlockedTitle(openThread)}>
                  <Button
                    size="sm"
                    variant="ghost"
                    icon="x"
                    disabled={queuedByCard.has(openThread.cardId) || removingCardId !== null}
                    onClick={() => requestRemove(openThread)}
                  >
                    {removingCardId === openThread.cardId ? 'Removing…' : 'Remove from task'}
                  </Button>
                </span>
              ) : (
                <span />
              )}
              <span style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {taskId ? (
                  <SpecQueueFromTarget
                    taskId={taskId}
                    title={openThread.cardName}
                    cardId={openThread.cardId}
                    trigger="button"
                    onEnqueued={handleEnqueued}
                  />
                ) : null}
                <a href={openThread.cardUrl} target="_blank" rel="noopener noreferrer">
                  <Button size="sm" variant="secondary" icon="external-link">
                    Open in Trello
                  </Button>
                </a>
              </span>
            </span>
          ) : undefined
        }
      >
        {openThread && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            {/* Live Trello state: description, attachments, full comment history */}
            <TrelloCardDetailBody cardId={openThread.cardId} />

            {/* Synced activity the inbox tracks — distinct from the Trello
                comment history above */}
            <div className="t-eyebrow" style={{ marginTop: 'var(--space-2)' }}>
              Activity ({openThread.items.length})
            </div>

            {openThread.items.map(item => (
              <div
                key={item.id}
                style={{
                  background: item.direction === 'outbound' ? 'var(--accent-soft)' : 'var(--surface-card)',
                  border: '1px solid var(--border-hairline)',
                  borderRadius: 'var(--radius-md)',
                  padding: '12px 14px',
                  opacity: item.direction === 'inbound' && item.status === 'cleared' ? 0.55 : 1,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '8px',
                    marginBottom: item.body ? '8px' : 0,
                  }}
                >
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontWeight: 500,
                      fontSize: 'var(--text-sm)',
                      color: 'var(--text-body)',
                    }}
                  >
                    {item.actor_name}
                    {item.direction === 'outbound' ? (
                      <Badge tone="up">Reply</Badge>
                    ) : (
                      <Badge tone={KIND_TONES[item.kind]}>{KIND_LABELS[item.kind]}</Badge>
                    )}
                  </span>
                  <span className="t-num" style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>
                    {timeAgo(item.occurred_at)}
                  </span>
                </div>
                {item.body && (
                  <div
                    style={{
                      fontSize: 'var(--text-sm)',
                      color: 'var(--text-muted)',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                  >
                    {item.body}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </SidePanel>

      {/* Above the layer-1 conversation panel (z 60/61), hence zIndex 100 */}
      <DeleteConfirmationModal
        isOpen={!!pendingRemove}
        onClose={() => {
          if (!removingCardId) setPendingRemove(null);
        }}
        onConfirm={confirmRemove}
        isDeleting={removingCardId !== null}
        title="Remove from task?"
        message={
          pendingRemove
            ? `This detaches “${pendingRemove.cardName}” from the task. The card stays in Client Comms and can be attached again from there.`
            : ''
        }
        confirmButtonLabel="Remove"
        processingLabel="Removing…"
        titleIcon={faLinkSlash}
        zIndex={100}
      />
    </>
  );
}
